import "../css/main.css";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const navMenuMq = window.matchMedia("(max-width: 767px)");
const pageLoader = document.querySelector("[data-page-loader]");
const LOADER_MIN_VISIBLE_MS = 600;
const PAGE_LEAVE_DELAY_MS = 320;
let loaderShownAt = Date.now();
let isPageLeaving = false;

let closeNavMenu = () => {};

function revealPageLoader({ immediate = false } = {}) {
  if (!pageLoader) {
    return;
  }
  pageLoader.classList.remove("is-hidden");
  if (immediate) {
    pageLoader.classList.add("is-active");
    return;
  }
  pageLoader.classList.remove("is-active");
  void pageLoader.offsetWidth;
  window.requestAnimationFrame(() => {
    pageLoader.classList.add("is-active");
  });
}

function hidePageLoader() {
  if (!pageLoader) {
    return;
  }
  const elapsed = Date.now() - loaderShownAt;
  const wait = Math.max(0, LOADER_MIN_VISIBLE_MS - elapsed);
  window.setTimeout(() => {
    pageLoader.classList.remove("is-active");
    pageLoader.classList.add("is-hidden");
    isPageLeaving = false;
  }, wait);
}

function resetPageTransitionState() {
  isPageLeaving = false;
  if (!pageLoader) {
    return;
  }
  pageLoader.classList.remove("is-active");
  pageLoader.classList.add("is-hidden");
}

function shouldHandlePageTransition(link) {
  if (!link || isPageLeaving) {
    return false;
  }
  if (link.hasAttribute("data-no-transition")) {
    return false;
  }
  const hrefAttr = link.getAttribute("href");
  if (!hrefAttr || hrefAttr.startsWith("#")) {
    return false;
  }
  if (
    link.target === "_blank" ||
    link.hasAttribute("download") ||
    link.getAttribute("rel")?.includes("external")
  ) {
    return false;
  }
  const nextUrl = new URL(link.href, window.location.href);
  if (nextUrl.origin !== window.location.origin) {
    return false;
  }
  if (nextUrl.href === window.location.href) {
    return false;
  }
  return true;
}

function initPageTransitions() {
  if (!pageLoader) {
    return;
  }

  revealPageLoader();
  hidePageLoader();

  window.addEventListener("pageshow", () => {
    resetPageTransitionState();
  });

  document.addEventListener(
    "click",
    (event) => {
    if (event.defaultPrevented) {
      return;
    }
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    const link = event.target.closest("a[href]");
    if (!shouldHandlePageTransition(link)) {
      return;
    }
    event.preventDefault();
    isPageLeaving = true;
    revealPageLoader({ immediate: true });
    const destination = link.href;
    window.setTimeout(() => {
      window.location.assign(destination);
      // Si la navigation échoue (ex. ancienne réponse « téléchargement »), débloquer l’UI.
      window.setTimeout(() => {
        if (isPageLeaving) {
          resetPageTransitionState();
        }
      }, 2000);
    }, PAGE_LEAVE_DELAY_MS);
    },
    true
  );
}

initPageTransitions();

function initNavMenu() {
  const root = document.querySelector(".menu[data-nav]");
  if (!root) {
    return;
  }
  const toggle = root.querySelector("[data-nav-toggle]");
  const panel = root.querySelector("[data-nav-panel]");
  const backdrop = root.querySelector("[data-nav-backdrop]");
  if (!toggle || !panel) {
    return;
  }

  const setOpen = (open) => {
    const useCompact = navMenuMq.matches;
    if (!useCompact && open) {
      return;
    }
    root.classList.toggle("is-nav-open", open);
    toggle.setAttribute("aria-expanded", String(open));
    toggle.setAttribute("aria-label", open ? "Fermer le menu" : "Ouvrir le menu");
    if (useCompact) {
      panel.setAttribute("aria-hidden", String(!open));
      if (open) {
        panel.removeAttribute("inert");
      } else {
        panel.setAttribute("inert", "");
      }
    } else {
      panel.removeAttribute("aria-hidden");
      panel.removeAttribute("inert");
    }
    document.body.style.overflow = open && useCompact ? "hidden" : "";
  };

  closeNavMenu = () => setOpen(false);

  toggle.addEventListener("click", () => {
    setOpen(!root.classList.contains("is-nav-open"));
  });

  if (backdrop) {
    backdrop.addEventListener("click", () => setOpen(false));
  }

  panel.querySelectorAll("a.navbar_link").forEach((anchor) => {
    anchor.addEventListener("click", () => {
      if (navMenuMq.matches) {
        setOpen(false);
      }
    });
  });

  navMenuMq.addEventListener("change", (e) => {
    if (!e.matches) {
      setOpen(false);
    }
  });

  setOpen(false);
}

initNavMenu();

const popupByHost = new WeakMap();

function closestPopupHost(el) {
  return el?.closest?.(".activites_pratique_item, .ressource_card");
}

function initPopupPortals() {
  document.querySelectorAll(".activites_pratique_item, .ressource_card").forEach((host) => {
    const popup = host.querySelector(".popup[data-popup]");
    if (!popup) {
      return;
    }
    popupByHost.set(host, popup);
    document.body.appendChild(popup);
  });
}

function openPopup(fromEl) {
  const host = closestPopupHost(fromEl);
  const popup = host ? popupByHost.get(host) : undefined;
  if (!popup) {
    return;
  }
  popup.classList.add("is-open");
  popup.style.display = "flex";
  document.body.style.overflow = "hidden";
}

function restoreBodyScrollAfterPopup() {
  if (document.querySelector(".popup[data-popup].is-open")) {
    return;
  }
  const navOpen = document.querySelector(".menu[data-nav].is-nav-open");
  if (!navOpen) {
    document.body.style.overflow = "";
  }
}

function closePopup(popup) {
  popup.classList.remove("is-open");
  popup.style.display = "none";
  restoreBodyScrollAfterPopup();
}

initPopupPortals();

document.addEventListener("click", (e) => {
  const opener = e.target.closest("[data-popup-open]");
  if (opener) {
    e.preventDefault();
    openPopup(opener);
    return;
  }
  const closeBtn = e.target.closest("[data-popup-close]");
  if (closeBtn) {
    const popup = closeBtn.closest(".popup[data-popup]");
    if (popup) {
      closePopup(popup);
    }
    return;
  }
  const popupBg = e.target.closest(".popup[data-popup].is-open");
  if (popupBg && e.target === popupBg) {
    closePopup(popupBg);
  }
});

document.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") {
    return;
  }
  document.querySelectorAll(".popup[data-popup].is-open").forEach(closePopup);
  closeNavMenu();
});

document.querySelectorAll("[data-popup-open]").forEach((el) => {
  el.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openPopup(el);
    }
  });
});

function initRessourcesFilters() {
  const root = document.querySelector(".ressources[data-ressources]");
  if (!root) {
    return;
  }

  const filters = Array.from(root.querySelectorAll("[data-filter]"));
  const cards = Array.from(root.querySelectorAll(".ressource_card[data-filters]"));
  if (!filters.length || !cards.length) {
    return;
  }

  const setActiveFilter = (filterKey) => {
    filters.forEach((btn) => {
      btn.classList.toggle("is-active", btn.dataset.filter === filterKey);
    });

    cards.forEach((card) => {
      if (filterKey === "__all") {
        card.style.display = "";
        return;
      }
      const cardFilters = (card.dataset.filters || "")
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
      const shouldShow = cardFilters.includes(filterKey);
      card.style.display = shouldShow ? "" : "none";
    });
  };

  const initialActive = filters.find((btn) => btn.dataset.filter === "__all") || filters[0];
  if (!initialActive) {
    return;
  }

  setActiveFilter(initialActive.dataset.filter);

  filters.forEach((btn) => {
    btn.addEventListener("click", () => {
      setActiveFilter(btn.dataset.filter);
    });
  });
}

initRessourcesFilters();

function scrollContactResultIntoView() {
  const section = document.querySelector(".section_text.is-contact.has-form-result");
  if (!section) {
    return;
  }

  const menu = document.querySelector(".menu");
  const menuOffset = menu?.offsetHeight ?? 0;
  const sectionTop = section.getBoundingClientRect().top + window.scrollY - menuOffset;
  const status = section.querySelector(".contact_status");
  const statusTop = status
    ? status.getBoundingClientRect().bottom + window.scrollY - window.innerHeight + 24
    : sectionTop;

  window.scrollTo({
    top: Math.max(0, sectionTop, statusTop),
    behavior: "auto",
  });
}

function initScrollRevealSections() {
  scrollContactResultIntoView();

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  const revealConfigs = [
    { selector: ".section_text, .section-text", target: ".text_container" },
    {
      selector: ".section_activities, .section-activities",
      target: ".activities_component, .activites_card",
      stagger: 0.12,
    },
    {
      selector: ".activites_item",
      target: ".activities_intro, .activites_pratique_item",
      stagger: 0.08,
    },
    {
      selector: ".ressources",
      target: ".ressources_header, .filters_row, .ressource_card",
      stagger: 0.08,
    },
    {
      selector: ".instagram",
      target: ".ressources_header, .instagram_feed, .button",
    },
    {
      selector: ".socials",
      target: ".social_line, .curve",
      stagger: 0.1,
    },
    { selector: ".section_cta, .section-cta", target: ".cta_component, .curve" },
    {
      selector: ".section_partners, .section-partenaires",
      target: ".partners_component, .partner_item",
      stagger: 0.08,
    },
  ];

  revealConfigs.forEach(({ selector, target, stagger = 0 }) => {
    document.querySelectorAll(selector).forEach((section) => {
      if (section.classList.contains("has-form-result")) {
        return;
      }

      const elements = target
        ? Array.from(section.querySelectorAll(target))
        : [section];
      if (!elements.length) {
        return;
      }

      gsap.from(elements, {
        y: 48,
        autoAlpha: 0,
        duration: 0.85,
        ease: "power2.out",
        stagger,
        scrollTrigger: {
          trigger: section,
          start: "top 82%",
          once: true,
        },
      });
    });
  });
}

initScrollRevealSections();

function initParallaxImages() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  const images = document.querySelectorAll("[data-parallax-image]");
  if (!images.length) {
    return;
  }

  images.forEach((image) => {
    const trigger =
      image.closest("[data-parallax-section]") ||
      image.closest("section, .hero") ||
      image.parentElement;
    if (!trigger) {
      return;
    }

    gsap.fromTo(
      image,
      { yPercent: -10 },
      {
        yPercent: 10,
        ease: "none",
        scrollTrigger: {
          trigger,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      }
    );
  });
}

initParallaxImages();

const HERO_GROUP_A = [0, 1, 2];
const HERO_GROUP_B = [3, 4, 5];
const HERO_SLOT_REPLACE_ORDER = [0, 2, 1];
const HERO_SLOT_INTERVAL = 2;
const HERO_SWAP_DURATION = 0.55;

function setHeroImage(img, entry) {
  img.src = entry.src;
  if (entry.srcset) {
    img.srcset = entry.srcset;
  } else {
    img.removeAttribute("srcset");
  }
}

function readHeroImages() {
  const dataEl = document.getElementById("hero-images-data");
  if (!dataEl) {
    return [];
  }
  try {
    return JSON.parse(dataEl.textContent);
  } catch {
    return [];
  }
}

function initHeroImageRotation() {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return;
  }

  const list = document.querySelector("[data-hero-rotation]");
  if (!list) {
    return;
  }

  const images = readHeroImages();
  if (!Array.isArray(images) || images.length < 6) {
    return;
  }

  const slotImgs = HERO_SLOT_REPLACE_ORDER.map((slotIndex) =>
    list.querySelector(`[data-hero-slot="${slotIndex}"]`)
  ).filter(Boolean);
  if (slotImgs.length !== 3) {
    return;
  }

  const swapSlot = (img, imageIndex) => {
    const entry = images[imageIndex];
    if (!entry?.src) {
      return gsap.timeline();
    }

    return gsap
      .timeline()
      .to(img, {
        autoAlpha: 0,
        y: 12,
        duration: HERO_SWAP_DURATION * 0.45,
        ease: "power2.in",
      })
      .add(() => setHeroImage(img, entry))
      .to(img, {
        autoAlpha: 1,
        y: 0,
        duration: HERO_SWAP_DURATION * 0.55,
        ease: "power2.out",
      });
  };

  const buildCycle = (indices) => {
    const cycle = gsap.timeline();
    slotImgs.forEach((img, step) => {
      const slotIndex = HERO_SLOT_REPLACE_ORDER[step];
      cycle.add(
        swapSlot(img, indices[slotIndex]),
        step === 0 ? 0 : `+=${HERO_SLOT_INTERVAL}`
      );
    });
    return cycle;
  };

  gsap.set(slotImgs, { autoAlpha: 1, y: 0 });

  gsap
    .timeline({ repeat: -1 })
    .add(buildCycle(HERO_GROUP_B))
    .add(buildCycle(HERO_GROUP_A), `+=${HERO_SLOT_INTERVAL}`);
}

initHeroImageRotation();

function initHeroScrollButton() {
  const button = document.querySelector(".hero_scroll-button");
  if (!button) {
    return;
  }

  button.addEventListener("click", (event) => {
    const href = button.getAttribute("href");
    if (!href?.startsWith("#")) {
      return;
    }

    const target = document.querySelector(href);
    if (!target) {
      return;
    }

    event.preventDefault();

    const menu = document.querySelector(".menu");
    const offset = menu?.offsetHeight ?? 0;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    window.scrollTo({
      top,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });

    history.pushState(null, "", href);
  });
}

initHeroScrollButton();

function initHashScrollOnLoad() {
  const { hash } = window.location;
  if (!hash?.startsWith("#activite-")) {
    return;
  }

  const scrollToTarget = () => {
    const target = document.querySelector(hash);
    if (!target) {
      return;
    }
    const menu = document.querySelector(".menu");
    const offset = menu?.offsetHeight ?? 0;
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    window.scrollTo({
      top,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  };

  if (document.readyState === "complete") {
    window.requestAnimationFrame(scrollToTarget);
  } else {
    window.addEventListener("load", scrollToTarget, { once: true });
  }
}

initHashScrollOnLoad();

const CURSOR_HOVER_SELECTOR =
  "a, .activites_pratique_item, .activites_card, .ressource_card";

function getCursorHoverTarget(element) {
  return element?.closest?.(CURSOR_HOVER_SELECTOR) ?? null;
}

function initCustomCursor() {
  if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
    return;
  }

  const cursor = document.createElement("div");
  cursor.className = "custom-cursor";
  cursor.setAttribute("aria-hidden", "true");
  document.body.appendChild(cursor);

  gsap.set(cursor, { xPercent: -50, yPercent: -50 });
  const xTo = gsap.quickTo(cursor, "x", { duration: 0.35, ease: "power3.out" });
  const yTo = gsap.quickTo(cursor, "y", { duration: 0.35, ease: "power3.out" });

  document.addEventListener("mousemove", (event) => {
    xTo(event.clientX);
    yTo(event.clientY);
  });

  document.addEventListener("mouseover", (event) => {
    if (getCursorHoverTarget(event.target)) {
      cursor.classList.add("is-hover-link");
    }
  });

  document.addEventListener("mouseout", (event) => {
    const fromTarget = getCursorHoverTarget(event.target);
    const toTarget = getCursorHoverTarget(event.relatedTarget);
    if (fromTarget && !toTarget) {
      cursor.classList.remove("is-hover-link");
    }
  });

  window.setTimeout(() => {
    cursor.classList.add("is-visible");
  }, 1000);
}

initCustomCursor();
